import each from 'lodash/each';
import invert from 'lodash/invert';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import replace from 'lodash/replace';

import { composeQuery, orderBy, select } from 'common/soql_builder';
import { OrderByDsl } from 'common/soql_builder/orderBy';
import { NamedSelect } from 'common/soql_builder/select';
import { SoQLType } from 'common/types/soql';
import { ViewColumn } from 'common/types/viewColumn';

import SoqlDataProvider from '../dataProviders/SoqlDataProvider';
import { ISetAgUniqueValuesQuery } from '../types/tableData';
import { CustomSortConfig, OrderConfig } from '../vif';
import { GROUP_COLUMN_PREFIX } from '../views/agGridReact/Constants';
import { UniqueValuesData } from '../views/agGridReact/types';
import { SERIES_TYPE_AG_GRID_TABLE } from '../views/SvgConstants';

import { mapSoqlRowsResponseToTable, whereClauseFilteringOwnColumn } from './SoqlHelpers';
import { createExportSelectAliasMap } from './TableDataExportHelpers';
import { buildCasesParts } from './TableDataHelpers';
import { unaliasRowData } from './TableDataQueryHelpers';
import { isCustomSortEnabled } from './VifSelectors';

function tableUniqueValuesQuery(
  columns: ViewColumn[],
  order: OrderConfig[],
  whereClause: string,
  customSortConfig: CustomSortConfig,
  limit?: number,
) {
  const selectAliasMap: { [key: string]: string } = createExportSelectAliasMap(columns);
  const selectQueries: NamedSelect[] = [];
  each(columns, (column) => {
    selectQueries.push(select(column, selectAliasMap[column.fieldName]));
  });
  const orderQueries: (OrderByDsl | undefined)[] = columns
    .map((column) => {
      if (column.dataTypeName === SoQLType.SoQLURLT) {
        return;
      }
      const columnOrderObject = order.find(
        (orderConfig) => column.fieldName === replace(orderConfig.columnName, `${GROUP_COLUMN_PREFIX}-`, ''),
      );
      if (!columnOrderObject) {
        return;
      }
      const ordering = columnOrderObject.ascending === true ? 'ascending' : 'descending';
      return orderBy(column, ordering);
    })
    .filter((orderByDsl) => !isUndefined(orderByDsl));

  const dataQuery = composeQuery({
    selects: selectQueries,
    distinct: true,
    orders: orderQueries,
    legacyWhereClause: whereClause,
    limit: limit,
    ...(isCustomSortEnabled() &&
      !isEmpty(customSortConfig) && {
        cases: buildCasesParts(selectQueries, customSortConfig, order),
      }),
  });

  return dataQuery;
}

export async function setAgUniqueValuesQuery({
  vif,
  columns,
  order,
  customSortConfig,
  limit,
}: ISetAgUniqueValuesQuery): Promise<UniqueValuesData> {
  const soqlDataProvider = SoqlDataProvider.fromVif(vif);
  const whereClause = whereClauseFilteringOwnColumn(vif, 0);
  const data: any = await soqlDataProvider.invokeSoqlQuery(tableUniqueValuesQuery, [
    columns,
    order,
    whereClause,
    customSortConfig,
    limit,
  ]);
  const aliasMap = createExportSelectAliasMap(columns);
  const unaliasedValues = unaliasRowData(data, invert(aliasMap));
  const soqlData: UniqueValuesData = mapSoqlRowsResponseToTable(
    columns,
    unaliasedValues,
    SERIES_TYPE_AG_GRID_TABLE,
  );
  return soqlData;
}
